import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, GraduationCap } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { binderyApi, type ThesisPrintRequest } from "@/lib/api/bindery";
import { ThesisStatusBadge } from "@/components/bindery/BinderyStatusBadge";
import {
  BINDING_TYPE_LABELS,
  DEGREE_LEVEL_LABELS,
  formatDate,
  formatMoney,
} from "@/pages/bindery/binderyFormatters";

export default function MyThesisRequestsPage() {
  const [requests, setRequests] = useState<ThesisPrintRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await binderyApi.thesis.mine({ limit: 50 });
      setRequests(res.requests);
    } catch (err) {
      console.error("Failed to load thesis requests", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PageContainer width="wide">
      <PageHeader
        title="My thesis requests"
        description="Submit and track thesis or dissertation print & bind orders."
        breadcrumbItems={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "My thesis requests" },
        ]}
        showSearch={false}
        titleSize="md"
        actions={
          <Button asChild>
            <Link to="/my/thesis/new">
              <Plus className="mr-2 h-4 w-4" /> New request
            </Link>
          </Button>
        }
      />

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : requests.length === 0 ? (
        <EmptyState
          icon={<GraduationCap className="h-6 w-6 text-slate-400" />}
          title="No thesis requests yet"
          description="Start a new request to print and bind your thesis or dissertation."
          action={
            <Button asChild>
              <Link to="/my/thesis/new">
                <Plus className="mr-2 h-4 w-4" /> New request
              </Link>
            </Button>
          }
        />
      ) : (
        <div className="grid gap-3">
          {requests.map((r) => (
            <Link key={r.id} to={`/my/thesis/${r.id}`}>
              <Card className="transition-all duration-200 hover:border-primary hover:shadow-sm">
                <CardContent className="flex flex-col gap-2 p-4 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-xs text-slate-500">{r.requestNumber}</p>
                    <p className="mt-0.5 line-clamp-1 font-medium text-slate-900">{r.title}</p>
                    <p className="text-xs text-slate-500">
                      {DEGREE_LEVEL_LABELS[r.degreeLevel]} · {BINDING_TYPE_LABELS[r.bindingType]} ·{" "}
                      {r.pageCount} pages × {r.copies} copies
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-slate-500">{formatDate(r.submittedAt)}</span>
                    <span className="font-medium tabular-nums">{formatMoney(r.cost)}</span>
                    <ThesisStatusBadge status={r.status} />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
